import { Component, EventEmitter, Inject } from '@angular/core';
import { FormService } from '@src/app/services/form/form.service';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { map, take, tap } from 'rxjs/operators';
import { GroupedFormData } from '@src/app/model/grouped-form-data';
import { PropertyField } from '@src/app/model/property-field';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';
import { Documentation } from '@src/app/model/documentation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-typed-form',
  templateUrl: './typed-form-modal.component.html',
  styleUrl: './typed-form-modal.component.scss',
  standalone: false,
})

export class TypedFormModalComponent {
  public waitingForResponse: boolean = false;
  public subjectName: string;
  public headerText: string;
  public submitText: string;
  public subjectTitle: string;
  public subjectId: string;
  public submit = new EventEmitter<JSON | JSON[]>();

  public documentation$: Observable<Documentation>;

  public formData: GroupedFormData;
  public formDataArray: GroupedFormData[];

  constructor(
    private _dialogRef: MatDialogRef<TypedFormModalComponent>,
    private _formService: FormService,
    private _apolloService: ApolloService,
    @Inject(MAT_DIALOG_DATA) private _data: TypedFormModalDataObject
  ) {
    this.subjectName = this._data.name;
    this.headerText = this._data.headerText;
    this.subjectTitle = this._data.subjectTitle;
    this.subjectId = this._data.id ? this._data.id.toString() : '';
    this.submitText = this._data.submitText;

    this.documentation$ = this._apolloService.getDocumentationBySubjectType$(this._data.inputType).pipe(
      take(1),
      map((documentation) => {
        if (documentation) {
          documentation.fields = this._skipFields(documentation.fields, this._data.skipFields);
          return documentation;
        }
      }),
      tap((documentation) => {
        if (documentation) {
          if (Array.isArray(this._data.input)) {
            const dataJsonInputArray = this._data.input.map(subject => subject.toGqlInput() as JSON);
            this.formDataArray = this._formService.createFormArray(documentation, dataJsonInputArray);
          } else {
            const dataJsonInput = this._data.input.toGqlInput() as JSON;
            this.formData = this._formService.createForm(documentation, dataJsonInput);
          }
        }
      }),
    );
  }

  public onSubmit(): void {
    this.waitingForResponse = true;

    const validationResult = Array.isArray(this._data.input)
      ? this._formService.validateFormArray(this.formDataArray)
      : this._formService.validateForm(this.formData.formGroup);

    if (validationResult.valid) {
      this.submitSubject();
    } else {
      this.showErrorAlert(validationResult.errors);
    }
  }

  submitSubject(): void {
    if (Array.isArray(this._data.input)) {
      const updatedData: JSON[] = this._formService.getFormArrayData(this.formDataArray);
      this.submit.emit(updatedData);
    } else {
      const updatedData: JSON = this._formService.getFormData(this.formData);
      this.submit.emit(updatedData);
    }
  }

  showErrorAlert(validationErrors: string[] = []): void {
    this.waitingForResponse = false;
    alert(`invalid form\n\n${validationErrors.join('\n')}`);
  }

  public closeDialog(): void {
    this._dialogRef.close();
  }

  private _skipFields(fieldObjects: PropertyField[], fieldsToSkip: string[]): PropertyField[] {
    return fieldObjects.filter(item => !fieldsToSkip?.includes(item.fieldName));
  }
}
